// src/utils/mockDataDrawingTasks.ts
// ─────────────────────────────────────────────────────────────────
// Phase 3 — Task Source Mock Data
//
// Three task sources beyond the existing MOCK_CONFIGURED_TASKS:
//   DrawingTask  — annotated tasks from IFC-status drawings
//   WIStepTask   — individual steps from Work Instructions
//   AITask       — AI-generated tasks from spec documents (DRAFT)
//
// All three are returned by taskService.getAllAssignableTasks()
// alongside existing PM-created tasks from MOCK_CONFIGURED_TASKS.
// ─────────────────────────────────────────────────────────────────

// ── Drawing Tasks ─────────────────────────────────────────────────
export interface DrawingTask {
  id:            string
  drawingId:     string
  drawingNumber: string
  drawingTitle:  string
  revision:      string
  annotationId:  string
  jobId:         string
  jobNumber:     string
  title:         string
  description:   string
  discipline:    string
  specClauseRef: string | null
  holdPoint:     string | null
  sequence:      number | null    // sequence dependency — must follow this task ID
  bomLineIds:    string[]         // which BOM lines this task requires
  photoRequired: boolean
  photoCount:    number
  addedAt:       string
}

export const MOCK_DRAWING_TASKS: DrawingTask[] = [
  {
    id:            'dt001',
    drawingId:     'dwg-j1-s01',
    drawingNumber: 'J1-S01',
    drawingTitle:  'Signal Pole Layout — Kessels / Gowan Intersection',
    revision:      'Rev 3',
    annotationId:  'ann-s01-001',
    jobId:         'j1',
    jobNumber:     'TP-2026-001',
    title:         'Install signal pole foundation — NW corner',
    description:   'Excavate to 900mm, install 200mm DIN base plate per J1-S01 Rev 3, backfill and compact to 98% standard compaction. Traffic control must be in place before excavation commences.',
    discipline:    'Civil',
    specClauseRef: 'MRTS50 cl. 6.3.4',
    holdPoint:     'HP-FOUND-001',
    sequence:      null,
    bomLineIds:    ['b6j1', 'b7j1'],
    photoRequired: true,
    photoCount:    3,
    addedAt:       '2026-06-10T09:00:00Z',
  },
  {
    id:            'dt002',
    drawingId:     'dwg-j1-c02',
    drawingNumber: 'J1-C02',
    drawingTitle:  'Conduit & Cable Pit Layout — Kessels Rd',
    revision:      'Rev 2',
    annotationId:  'ann-c02-001',
    jobId:         'j1',
    jobNumber:     'TP-2026-001',
    title:         'Cable pit installation — Kessels / Gowan NW corner',
    description:   'Install 600x450x600 UPVC cable pit to J1-C02 Rev 2. Set to line and level. Seal all conduit entries. Confirm depth matches drawing before backfilling.',
    discipline:    'Civil',
    specClauseRef: 'MRTS50 cl. 7.1',
    holdPoint:     null,
    sequence:      null,
    bomLineIds:    ['b7j1'],
    photoRequired: true,
    photoCount:    2,
    addedAt:       '2026-06-10T09:05:00Z',
  },
  {
    id:            'dt003',
    drawingId:     'dwg-j1-e01',
    drawingNumber: 'J1-E01',
    drawingTitle:  'Electrical Schematic — Kessels Rd Controller',
    revision:      'Rev 4',
    annotationId:  'ann-e01-001',
    jobId:         'j1',
    jobNumber:     'TP-2026-001',
    title:         'Cable pulling — conduit run NW corner to controller cabinet',
    description:   'Pull 6mm T+E per J1-E01 Rev 4. Maintain minimum bend radius of 150mm. Label both ends immediately after pulling. Do not energise until ITP hold point HP-CABLE-001 signed off.',
    discipline:    'Electrical',
    specClauseRef: 'AS/NZS 3000 cl. 3.10',
    holdPoint:     'HP-CABLE-001',
    sequence:      null,
    bomLineIds:    ['b1j1', 'b2j1'],
    photoRequired: true,
    photoCount:    4,
    addedAt:       '2026-06-10T09:10:00Z',
  },
  {
    id:            'dt004',
    drawingId:     'dwg-j2-its-01',
    drawingNumber: 'J2-ITS-01',
    drawingTitle:  'ITS Controller Cabinet Layout — Pacific Mwy Station A',
    revision:      'Rev B',
    annotationId:  'ann-its01-001',
    jobId:         'j2',
    jobNumber:     'TP-2026-002',
    title:         'SCATS controller commissioning — Station A',
    description:   'Commission SCATS controller per J2-ITS-01 Rev B and QD-TSG-0026 Rev B. Record firmware version, serial numbers, and initial phase timing. Network operator must witness energisation.',
    discipline:    'Electrical',
    specClauseRef: 'QD-TSG-0026 Rev B cl. 4.2',
    holdPoint:     'HP-ITS-001',
    sequence:      null,
    bomLineIds:    ['b3j2'],
    photoRequired: true,
    photoCount:    5,
    addedAt:       '2026-06-12T10:00:00Z',
  },
  {
    id:            'dt005',
    drawingId:     'dwg-j4-hv-001',
    drawingNumber: 'J4-HV-001',
    drawingTitle:  'HV Cable Alignment — Annerley Rd',
    revision:      'Rev 1',
    annotationId:  'ann-hv001-001',
    jobId:         'j4',
    jobNumber:     'TP-2026-004',
    title:         'HV cable joint termination — Pit 4A to Pit 4B',
    description:   'Terminate 33kV XLPE cable per J4-HV-001 and Energex TEC-001 Rev 3. Install heat-shrink joints. Network operator authorisation required before energisation. FM check: confirm minimum 10h rest from previous shift.',
    discipline:    'HV Electrical',
    specClauseRef: 'Energex TEC-001 Rev 3',
    holdPoint:     'HP-HV-001',
    sequence:      null,
    bomLineIds:    [],
    photoRequired: true,
    photoCount:    5,
    addedAt:       '2026-06-10T11:00:00Z',
  },
]

// ── Work Instruction Step Tasks ───────────────────────────────────
export interface WIStepTask {
  id:              string
  wiId:            string
  wiTitle:         string
  wiRef:           string           // e.g. 'WI-J1-003'
  stepNumber:      number
  stepTitle:       string
  stepDescription: string
  holdPointClass:  'WITNESS' | 'HOLD' | 'REVIEW' | null
  holdPointRef:    string | null
  jobId:           string
  jobNumber:       string
  discipline:      string
  prerequisiteStepId: string | null
  bomLineIds:      string[]
  photoRequired:   boolean
  signOffRequired: boolean          // crew must sign off in app to mark complete
  addedAt:         string
}

export const MOCK_WI_STEP_TASKS: WIStepTask[] = [
  {
    id:              'wt001',
    wiId:            'wi-pkg-001',
    wiTitle:         'Signal Pole Foundation — Kessels Rd',
    wiRef:           'WI-J1-001',
    stepNumber:      1,
    stepTitle:       'Pre-excavation survey',
    stepDescription: 'Dial Before You Dig check completed and results reviewed by supervisor. Mark out exclusion zone. Confirm no conflicts with utility plans.',
    holdPointClass:  'REVIEW',
    holdPointRef:    'HP-PREX-001',
    jobId:           'j1',
    jobNumber:       'TP-2026-001',
    discipline:      'Civil',
    prerequisiteStepId: null,
    bomLineIds:      [],
    photoRequired:   true,
    signOffRequired: true,
    addedAt:         '2026-06-10T09:00:00Z',
  },
  {
    id:              'wt002',
    wiId:            'wi-pkg-001',
    wiTitle:         'Signal Pole Foundation — Kessels Rd',
    wiRef:           'WI-J1-001',
    stepNumber:      2,
    stepTitle:       'Excavation and base plate installation',
    stepDescription: 'Excavate to design depth (900mm), install DIN base plate, check verticality using spirit level. No concrete pour until HP-FOUND-001 witnessed.',
    holdPointClass:  'HOLD',
    holdPointRef:    'HP-FOUND-001',
    jobId:           'j1',
    jobNumber:       'TP-2026-001',
    discipline:      'Civil',
    prerequisiteStepId: 'wt001',
    bomLineIds:      ['b6j1', 'b7j1'],
    photoRequired:   true,
    signOffRequired: true,
    addedAt:         '2026-06-10T09:05:00Z',
  },
  {
    id:              'wt003',
    wiId:            'wi-pkg-002',
    wiTitle:         'ITS Controller Installation — J2 Station A',
    wiRef:           'WI-J2-001',
    stepNumber:      1,
    stepTitle:       'Cabinet pre-installation check',
    stepDescription: 'Confirm cabinet dimensions against J2-ITS-01. Verify mounting position and clearances. No installation until cabinet position confirmed by PM.',
    holdPointClass:  'WITNESS',
    holdPointRef:    'HP-CAB-001',
    jobId:           'j2',
    jobNumber:       'TP-2026-002',
    discipline:      'Electrical',
    prerequisiteStepId: null,
    bomLineIds:      ['b3j2'],
    photoRequired:   true,
    signOffRequired: true,
    addedAt:         '2026-06-12T10:00:00Z',
  },
  {
    id:              'wt004',
    wiId:            'wi-pkg-002',
    wiTitle:         'ITS Controller Installation — J2 Station A',
    wiRef:           'WI-J2-001',
    stepNumber:      2,
    stepTitle:       'Controller mounting and initial wiring',
    stepDescription: 'Mount SCATS controller to DIN rail. Connect power supply and communications module. Verify all connections before applying power. Record batch numbers.',
    holdPointClass:  null,
    holdPointRef:    null,
    jobId:           'j2',
    jobNumber:       'TP-2026-002',
    discipline:      'Electrical',
    prerequisiteStepId: 'wt003',
    bomLineIds:      ['b3j2'],
    photoRequired:   true,
    signOffRequired: false,
    addedAt:         '2026-06-12T10:05:00Z',
  },
]

// ── AI-Generated Tasks ────────────────────────────────────────────
export type AITaskStatus = 'DRAFT' | 'APPROVED' | 'REJECTED'

export interface AITask {
  id:           string
  aiJobRef:     string             // AI compliance scan reference
  sourceDocRef: string             // e.g. 'MRTS50 cl. 9.2', 'AS/NZS 3000'
  sourceDocTitle:string
  jobId:        string
  jobNumber:    string
  title:        string
  description:  string
  rationale:    string             // why the AI flagged this as needed
  discipline:   string
  status:       AITaskStatus
  approvedById: string | null
  approvedAt:   string | null
  rejectedById: string | null
  rejectedAt:   string | null
  generatedAt:  string
}

export const MOCK_AI_TASKS: AITask[] = [
  {
    id:            'ai001',
    aiJobRef:      'AI-SCAN-J1-001',
    sourceDocRef:  'MRTS50 cl. 9.2',
    sourceDocTitle:'MRTS50 — Traffic Signal Equipment and Installation',
    jobId:         'j1',
    jobNumber:     'TP-2026-001',
    title:         'Signal head alignment verification — post-installation check',
    description:   'Verify alignment of all signal heads using sighting jig or surveying instrument. Confirm horizontal and vertical tolerances are within MRTS50 cl. 9.2 limits (±1° horizontal, ±0.5° vertical). Record actual readings.',
    rationale:     'MRTS50 cl. 9.2 mandates post-installation alignment checks before client sign-off. No alignment check task was found in the existing task list for this job.',
    discipline:    'Electrical',
    status:        'DRAFT',
    approvedById:  null,
    approvedAt:    null,
    rejectedById:  null,
    rejectedAt:    null,
    generatedAt:   '2026-07-10T08:00:00Z',
  },
  {
    id:            'ai002',
    aiJobRef:      'AI-SCAN-J1-001',
    sourceDocRef:  'AS/NZS 3000:2018 cl. 8.3',
    sourceDocTitle:'Wiring Rules — AS/NZS 3000:2018',
    jobId:         'j1',
    jobNumber:     'TP-2026-001',
    title:         'Insulation resistance test — new cable installation',
    description:   'Perform insulation resistance test on all new cable runs per AS/NZS 3000 cl. 8.3. Minimum IR value: 1 MΩ at 500V DC. Record megger readings on test sheet with date, time, and tester ID.',
    rationale:     'AS/NZS 3000 requires IR testing before commissioning any new installation. No electrical test task was identified in the existing task list for J1.',
    discipline:    'Electrical',
    status:        'APPROVED',
    approvedById:  'u4',
    approvedAt:    '2026-07-10T09:00:00Z',
    rejectedById:  null,
    rejectedAt:    null,
    generatedAt:   '2026-07-10T08:00:00Z',
  },
  {
    id:            'ai003',
    aiJobRef:      'AI-SCAN-J2-001',
    sourceDocRef:  'QD-TSG-0026 Rev B cl. 7',
    sourceDocTitle:'QD-TSG-0026 — SCATS Signal Controller Specification',
    jobId:         'j2',
    jobNumber:     'TP-2026-002',
    title:         'SCATS software configuration backup — pre-commissioning',
    description:   'Export SCATS configuration file before first energisation. Store backup in project document register. Naming convention: [JobNo]-SCATS-CFG-[DATE]-PRE.dat.',
    rationale:     'QD-TSG-0026 cl. 7.1 requires configuration backup before commissioning. No backup task found in J2 task list.',
    discipline:    'Electrical',
    status:        'DRAFT',
    approvedById:  null,
    approvedAt:    null,
    rejectedById:  null,
    rejectedAt:    null,
    generatedAt:   '2026-07-10T08:05:00Z',
  },
]
